import React from 'react'
import styled, { css } from 'styled-components'
import {
  ariaAttr,
  isNumber,
  useCallbackRef,
  useEventListener,
  useMergeRefs,
  useUnmountEffect
} from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import {
  calculateNodeHeight,
  getSizingData,
  removeHiddenTextarea
} from './utils'

export const TextArea = React.forwardRef(
  (
    {
      rows,
      cacheMeasurements,
      minRows = 5,
      maxRows = 10,
      onChange: onChangeProp,
      onHeightChange: onHeightChangeProp,
      placeholder,
      resize = 'none',
      maxWidth = 700,
      disabled = false,
      invalid = false,
      required = false,
      readOnly = false,
      ...rest
    },
    ref
  ) => {
    const textareaRef = React.useRef(null)
    const heightRef = React.useRef(0)
    const measurementsCacheRef = React.useRef()
    const refs = useMergeRefs(ref, textareaRef)
    const onChange = useCallbackRef(onChangeProp)
    const onHeightChange = useCallbackRef(onHeightChangeProp)

    if (__DEV__ && rest.style) {
      if ('maxHeight' in rest.style) {
        console.warn(
          'Using `style.maxHeight` for <Textarea/> is not supported. Please use `maxRows`.'
        )
      }
      if ('minHeight' in rest.style) {
        console.warn(
          'Using `style.minHeight` for <Textarea/> is not supported. Please use `minRows`.'
        )
      }
    }

    const resizeTextarea = React.useCallback(() => {
      if (resize !== 'none') return

      const node = textareaRef.current
      const nodeSizingData =
        cacheMeasurements && measurementsCacheRef.current
          ? measurementsCacheRef.current
          : getSizingData(node)

      if (!nodeSizingData) {
        return
      }

      measurementsCacheRef.current = nodeSizingData

      const [height, rowHeight] = calculateNodeHeight(
        nodeSizingData,
        node.value || node.placeholder || 'x',
        rows || minRows,
        rows || maxRows
      )

      if (heightRef.current !== height) {
        heightRef.current = height
        node.style.setProperty('height', `${height}px`, 'important')
        onHeightChange?.(height, { rowHeight })
      }
    }, [resize, cacheMeasurements, rows, minRows, maxRows, onHeightChange])

    const handleChange = React.useCallback(
      event => {
        resizeTextarea()
        onChange?.(event)
      },
      [resizeTextarea, onChange]
    )

    React.useLayoutEffect(resizeTextarea)
    useEventListener('resize', resizeTextarea, window, {
      enabled: resize === 'none'
    })
    useUnmountEffect(removeHiddenTextarea)

    return (
      <Root
        {...rest}
        ref={refs}
        placeholder={placeholder}
        onChange={handleChange}
        resize={resize}
        maxWidth={maxWidth}
        disabled={disabled}
        invalid={invalid}
        required={required}
        readOnly={readOnly}
        aria-invalid={ariaAttr(invalid)}
        aria-required={ariaAttr(required)}
        aria-readonly={ariaAttr(readOnly)}
        aria-placeholder={ariaAttr(placeholder)}
        aria-multiline={true}
      />
    )
  }
)

if (__DEV__) {
  TextArea.displayName = 'TextArea'
}

const Root = styled.textarea`
  width: 100%;
  color: #25252d;
  font-size: 15px;
  line-height: 120%;
  font-weight: normal;
  padding: 8px 16px;
  appearance: none;
  border-radius: 4px;
  background: #f6f6f9;
  border: 1px solid #d1d1e0;
  max-width: ${({ maxWidth }) =>
    isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};
  resize: ${({ resize }) => resize};
  transition-duration: 150ms;
  transition-timing-function: ease-in-out;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  &::placeholder {
    color: #b2b2cc;
    font-weight: normal;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:hover {
    border-color: #b2b2cc;
  }

  &:active,
  &:focus {
    outline: none;
    border: 1px solid #ac0ae8;
    box-shadow: 0 0 0 1px #ac0ae8;
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  ${({ invalid, disabled }) =>
    !disabled &&
    invalid &&
    css`
      border-color: #f44336;

      &:hover {
        border-color: #f44336;
      }

      &:active,
      &:focus {
        outline: none;
        border-color: #f44336;
        box-shadow: 0 0 0 1px #f44336;
      }
    `};
`
